import { FullPageLoader } from '@app/shared/components/full-page-loader';
import { PUBLIC_PATH } from '@app/shared/constants/paths.constant';
import { Roles } from '@app/shared/constants/roles.constant';
import { useAuthHydrated } from '@app/shared/hooks/use-auth-hydrated';
import { useAuthStore } from '@app/shared/store/auth.store';
import { useMenuStore } from '@app/shared/store/menu.store';
import { resolveAuthRedirect } from '@app/shared/utils/auth-redirect.utils';
import { FC, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

interface SadminGuardProps {
  children: ReactNode;
}

export const SadminGuard: FC<SadminGuardProps> = ({ children }) => {
  const hasHydrated = useAuthHydrated();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const menu = useMenuStore((state) => state.menu);
  const location = useLocation();

  // Zustand v5 persist rehydrates in a microtask — the first render
  // sees `isAuthenticated=false` even when a valid sadmin session
  // is on disk. Bouncing to /auth/login here would visibly log the
  // operator out on every hard refresh of a /sadmin/* route. Wait
  // until hydration settles before deciding.
  if (!hasHydrated) {
    return <FullPageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to={PUBLIC_PATH.LOGIN} state={{ from: location }} />;
  }

  // Only super_admin users can access /sadmin routes. Bounce to the
  // first route the caller can actually reach — hardcoding /dashboard
  // dumped roles without `dashboard.view` on the AccessDeniedScreen.
  if (user?.role?.name !== Roles.SuperAdmin) {
    return <Navigate to={resolveAuthRedirect(user, menu)} />;
  }

  return children;
};

export default SadminGuard;
